import { Draft, produce } from 'immer';
import { calcInferenceSetDerivatives } from './inference/calcInferenceSetDerivatives';
import { calcCarePhaseCostDerivatives } from './carePhases/calcCarePhaseCostDerivatives';
import { calcSupportProviderDerivatives } from './supportProviders/calcSupportProviderDerivatives';
import { calcSurveyDerivatives } from './survey/calcSurveyDerivatives';
import { fullName } from '../fullName';
import { calcCareEnvironmentsDerivatives } from './careEnvironments/calcCareEnvironmentsDerivatives';
import { calcFundingSourceDerivatives } from './funding/calcFundingSourceDerivatives';
import { applyDiscountsToCareEnvironmentCosts } from '../applyDiscountsToCareEnvironmentCosts';
import { evaluateClientInsights } from './insights/evaluateClientInsights';
import { cloneDeep } from 'lodash';
import { CalendarDate, parseCalendarDate, toCalendarDate } from './utils/resolveAsOfDate';

// Options that tune the derivative calc pipeline.
export interface ClientDerivativeOptions {
  // Overrides the claim-projection engine gate for this pass only, without
  // touching the client. Preview and comparison flows use it to force a model
  // (e.g. the short-term-care quote preview, which refuses to report a legacy
  // approximation). Omitted, the gate resolves from the client's
  // `projectionEngineEnabled`, which defaults ON.
  useClaimProjectionEngine?: boolean;

  // Pins "today" for the whole pass, as a `YYYY-MM-DD` calendar date. Every
  // date-dependent calc — client age, the care-cost inflation anchor,
  // premium/contribution year defaults — reads this (via `resolveAsOfDate`)
  // instead of calling `new Date()`, so the same inputs produce the same output
  // no matter when OR WHERE the calc runs. A `Date` is deliberately not accepted:
  // the calc reads local getters, so an instant means different calendar days in
  // different timezones (see `CalendarDate`). Convert with `toCalendarDate`.
  // Default: today, so every existing caller is unchanged.
  asOfDate?: CalendarDate;
}

export function calcClientDerivatives(
  mutableClient: Draft<Client>,
  clientPartner: Client | null,
  options: ClientDerivativeOptions = {},
): Draft<Client> {
  return produce(mutableClient, draftClient => {
    if (clientPartner) {
      addMutableClientPartner(draftClient, clientPartner);
    }
    runAllClientDerivativeCalculations(draftClient, options);
  });
}

function runAllClientDerivativeCalculations(
  mutableClient: Draft<Client>,
  options: ClientDerivativeOptions,
): Draft<Client> {
  calcClientDerivativesImpl(mutableClient, options);
  return mutableClient;
}

export function calcClientDerivativesImpl(
  mutableClient: Draft<Client>,
  options: ClientDerivativeOptions = {},
): Draft<Client> {
  mutableClient.clientFullName = fullName(mutableClient);
  // Deliberately real time, NOT `asOfDate`: this doubles as the cache-busting
  // token in `recommendCareEnvironment`'s memo key, so pinning it would let a
  // stale recommendation survive a pass that ran on changed survey answers.
  mutableClient.cachedTimestamp = new Date();

  // Single source-of-truth "today" for the whole pass. Pinned here, before any
  // phase-cost or ledger calc, so the Stage-1 phase costs and the new-engine
  // ledger anchor care-cost inflation at the SAME current year by construction
  // (P1-7: the two layers used to read different anchor years and drift by
  // `(1+i)^N` for a client added N years ago). Set once per derivative pass so a
  // single calc can't straddle a month or year boundary mid-pipeline, and read
  // downstream via `resolveAsOfDate` rather than each site calling `new Date()`.
  const asOfDate = options.asOfDate ?? toCalendarDate(new Date());
  mutableClient.calcAsOfDate = asOfDate;
  mutableClient.costInflationAnchorYear = parseCalendarDate(asOfDate).getFullYear();

  calcSurveyDerivatives(mutableClient); // must be first
  calcCareEnvironmentsDerivatives(mutableClient);
  calcSupportProviderDerivatives(mutableClient, options);
  calcInferenceSetDerivatives(mutableClient);
  applyDiscountsToClient(mutableClient);
  calcCarePhaseCostDerivatives(mutableClient, options);
  calcFundingSourceDerivatives(mutableClient, options);
  mutableClient.clientInsights = evaluateClientInsights(mutableClient);

  return mutableClient;
}

/**
 * Re-applies discounts based on current funding sources.
 * This is needed for modeling the impact of potential funding sources on care environment costs.
 * Note: This is already performed on the server side in fetchClientLocalCareEnvironmentCosts.
 */
function applyDiscountsToClient(mutableClient: Draft<Client>): void {
  if (!mutableClient.clientLocalCareEnvironmentCosts) {
    return;
  }

  mutableClient.clientLocalCareEnvironmentCosts = applyDiscountsToCareEnvironmentCosts(
    mutableClient.clientLocalCareEnvironmentCosts as LocalCareEnvironmentCosts,
    mutableClient.multipleFundingSources,
  ) as Draft<LocalCareEnvironmentCosts>;
}

function addMutableClientPartner(mutableClient: Draft<Client>, clientPartner: Client): void {
  const clientPartnerCopy = cloneDeep(clientPartner);
  mutableClient.mutableClientPartner = clientPartnerCopy;
}
